import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from '../../legacy/core/Button';
import Chip from '../Chip';
import SelectDropdown, { SELECT_DROPDOWN_SIZE } from '../SelectDropdown';
import { useTranslations } from '../../context/TranslationContext';
import useRemoteOptions from '@hooks/useRemoteOptions';
import { INLINE_ADD_SELECT_ICON, INLINE_ADD_SELECT_KEY, INLINE_ADD_SELECT_MIN_INPUT_LENGTH } from './constants';

const InlineAddSelectField = ({ url, options, minSearchLength, selectRef, ...selectProps }) => {
    const remote = useRemoteOptions(url, { minSearchLength, knownOptions: options });
    return (
        <SelectDropdown
            ref={selectRef}
            {...selectProps}
            minSearchLength={minSearchLength}
            {...(url
                ? { options: remote.options, loading: remote.loading, loadFailed: remote.failed, onSearch: remote.onSearch }
                : { options })}
        />
    );
};

/**
 * A "+ Add" link that expands into a searchable select in place. Picking an option calls
 * `onChange(value, data, close)`; the panel stays open, showing a saving chip while `loading`,
 * until the consumer calls `close()`. Escape or an outside click collapses without a pick.
 * Focus returns to the Add button when the panel closes from inside it.
 */
const InlineAddSelect = ({
    onChange = () => {},
    options,
    url,
    tags = true,
    loading = false,
    disabled = false,
    placeholder,
    minSearchLength = INLINE_ADD_SELECT_MIN_INPUT_LENGTH,
    createTagFormat,
    size = SELECT_DROPDOWN_SIZE.SM,
    portal = true,
    labels = {},
    className,
    id,
    buttonClassName,
    ...rest
}) => {
    const { t } = useTranslations();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const selectRef = useRef(null);

    const close = useCallback(() => {
        const focusWasInside = wrapRef.current?.contains(document.activeElement) || document.activeElement === document.body;
        setOpen(false);
        if (focusWasInside) {
            setTimeout(() => wrapRef.current?.querySelector('.ds-inline-add-select__button')?.focus(), 0);
        }
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            // The select portals its menu to <body>, so an option is outside wrapRef:
            // closing here would unmount the select before react-select's click lands.
            const { target } = event;
            if (target?.closest?.('.ds-select-dropdown__menu-portal')) return;
            if (wrapRef.current && !wrapRef.current.contains(target)) setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === INLINE_ADD_SELECT_KEY.ESCAPE) close();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, close]);

    useEffect(() => {
        if (open && !loading) {
            const timer = setTimeout(() => selectRef.current?.focusInput?.(), 0);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [open, loading]);

    const addLabel = labels.add || t('Add');
    const savingLabel = labels.saving || t('Saving...');

    const handleChange = (value, meta) => {
        if (value == null || value === '') return;
        onChange(value, meta, close);
    };

    return (
        <div className={classNames('ds-inline-add-select', { 'ds-inline-add-select--open': open }, className)} ref={wrapRef} {...rest}>
            {open && (
                <div className="ds-inline-add-select__panel">
                    {loading ? (
                        <Chip value={savingLabel} showLoader />
                    ) : (
                        <InlineAddSelectField
                            selectRef={selectRef}
                            id={id}
                            size={size}
                            options={options}
                            url={url}
                            tags={tags}
                            searchable
                            clearOnChange
                            placeholder={placeholder || t('Search...')}
                            minSearchLength={minSearchLength}
                            createTagFormat={createTagFormat || t("Create new '{term}'")}
                            onChange={handleChange}
                            portal={portal}
                            className="ds-inline-add-select__select"
                        />
                    )}
                </div>
            )}
            <Button
                className={classNames('ds-inline-add-select__button', buttonClassName)}
                type="link-secondary-hover"
                size="xs"
                icon={INLINE_ADD_SELECT_ICON.ADD}
                title={addLabel}
                disabled={disabled}
                aria-expanded={open}
                onClick={(event) => {
                    event.preventDefault();
                    if (!disabled) setOpen(true);
                }}
            />
        </div>
    );
};

InlineAddSelect.propTypes = {
    /** `(value, { data }, close)` when an option is picked or a tag created */
    onChange: PropTypes.func,
    /** Static options (see SelectDropdown) */
    options: PropTypes.array,
    /** Remote option source, `GET url?term=…` returning select2 `{ results }` (via useRemoteOptions) */
    url: PropTypes.string,
    /** Allow creating a new value from the typed term */
    tags: PropTypes.bool,
    /** A pick is being saved: the select is replaced by a saving chip */
    loading: PropTypes.bool,
    /** Disable the add button */
    disabled: PropTypes.bool,
    /** Search placeholder */
    placeholder: PropTypes.string,
    /** Typed characters before searching */
    minSearchLength: PropTypes.number,
    /** Label of the create row; `{term}` is replaced with the typed text */
    createTagFormat: PropTypes.string,
    /** SelectDropdown size */
    size: PropTypes.oneOf(Object.values(SELECT_DROPDOWN_SIZE)),
    /** Render the option menu in a body portal so a scrolling or clipping ancestor cannot cut it off */
    portal: PropTypes.bool,
    /** Translated texts: `{ add, saving }` */
    labels: PropTypes.shape({ add: PropTypes.string, saving: PropTypes.string }),
    /** Extra class on the wrapper */
    className: PropTypes.string,
    /** Id forwarded to the select */
    id: PropTypes.string,
    /** Extra class on the add button */
    buttonClassName: PropTypes.string,
};

export { INLINE_ADD_SELECT_ICON, INLINE_ADD_SELECT_MIN_INPUT_LENGTH, INLINE_ADD_SELECT_KEY };
export default InlineAddSelect;
